import React, { useEffect, useState, isValidElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSidebar } from '../../context/SidebarContext';
import Logo from '../Logo';
import {
  DashboardIcon,
  NotificationIcon,
  ClientsIcon,
  InvoicesIcon,
  OrdersIcon,
  MaterialsIcon,
  InventoryIcon,
  SuppliersIcon,
  PurchasesIcon,
  ReportIcon,
  ChartPieIcon,
  FinanceIcon,
  UsersIcon,
  SettingsIcon,
  StoreIcon,
  LayoutIcon,
  GridIcon,
  LayersIcon,
  TruckIcon,
  LogoutIcon,
  GlobeIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ArrowUpIcon
} from '../common/SvgIcons';
import './Sidebar.css';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const { isOpen, isCollapsed, toggleCollapse, closeSidebar } = useSidebar();
  const { t, isRTL, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const collapsed = isCollapsed;
  const [expandedSections, setExpandedSections] = useState({
    sales: true,
    inventory: true,
    reports: true,
    coveraty: true,
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Close sidebar on mobile when clicking a link
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      closeSidebar();
    }
  }, [location.pathname]);

  if (!user) {
    return null;
  }

  // Define menu items using unified SVG icons and translations
  const getMenuItems = () => {
    const baseItems = [
      {
        section: 'main',
        items: [
          { path: '/dashboard', label: t('nav.dashboard'), icon: <DashboardIcon className="w-5 h-5" />, roles: ['admin', 'receptionist', 'designer', 'worker', 'financial'] },
          { path: '/notifications', label: t('nav.notifications'), icon: <NotificationIcon className="w-5 h-5" />, roles: ['admin', 'receptionist', 'designer', 'worker', 'financial'] },
        ],
      },
    ];

    const salesSection = {
      section: 'sales',
      label: t('nav.sales'),
      icon: <OrdersIcon className="w-5 h-5" />,
      collapsible: true,
      items: [
        { path: '/clients', label: t('nav.clients'), icon: <ClientsIcon className="w-5 h-5" />, roles: ['admin', 'receptionist'] },
        { path: '/invoices', label: t('nav.invoices'), icon: <InvoicesIcon className="w-5 h-5" />, roles: ['admin', 'designer', 'financial'] },
        { path: '/orders', label: t('nav.orders'), icon: <OrdersIcon className="w-5 h-5" />, roles: ['admin', 'designer', 'worker', 'financial'] },
      ],
    };

    const inventorySection = {
      section: 'inventory',
      label: t('nav.inventory'),
      icon: <InventoryIcon className="w-5 h-5" />,
      collapsible: true,
      items: [
        { path: '/materials', label: t('nav.materials'), icon: <MaterialsIcon className="w-5 h-5" />, roles: ['admin'] },
        { path: '/products', label: t('nav.products'), icon: <OrdersIcon className="w-5 h-5" />, roles: ['admin', 'designer'] },
        { path: '/inventory', label: t('nav.stockManagement'), icon: <InventoryIcon className="w-5 h-5" />, roles: ['admin', 'worker'] },
        { path: '/material-withdrawal', label: t('nav.materialWithdrawal'), icon: <ArrowUpIcon className="w-5 h-5" />, roles: ['admin', 'worker'] },
        { path: '/suppliers', label: t('nav.suppliers'), icon: <SuppliersIcon className="w-5 h-5" />, roles: ['admin'] },
        { path: '/purchases', label: t('nav.purchases'), icon: <PurchasesIcon className="w-5 h-5" />, roles: ['admin'] },
      ],
    };

    const reportsSection = {
      section: 'reports',
      label: t('nav.reports'),
      icon: <ReportIcon className="w-5 h-5" />,
      collapsible: true,
      items: [
        { path: '/reports/client-analytics', label: t('nav.clientAnalytics'), icon: <ChartPieIcon className="w-5 h-5" />, roles: ['admin', 'financial'] },
        { path: '/financial-report', label: t('nav.financialReport'), icon: <ReportIcon className="w-5 h-5" />, roles: ['admin', 'financial'] },
        { path: '/financial-stats', label: t('nav.financialStats'), icon: <FinanceIcon className="w-5 h-5" />, roles: ['admin', 'financial'] },
        { path: '/client-reports', label: t('nav.clientReports'), icon: <InvoicesIcon className="w-5 h-5" />, roles: ['admin', 'financial'] },
      ],
    };

    const settingsSection = {
      section: 'settings',
      label: t('nav.settings'),
      icon: <SettingsIcon className="w-5 h-5" />,
      collapsible: false,
      items: [
        { path: '/users', label: t('nav.userManagement'), icon: <UsersIcon className="w-5 h-5" />, roles: ['admin'] },
        { path: '/website-settings', label: t('nav.websiteSettings'), icon: <SettingsIcon className="w-5 h-5" />, roles: ['admin'] },
      ],
    };

    const coveratySection = {
      section: 'coveraty',
      label: t('nav.coveraty'),
      icon: <StoreIcon className="w-5 h-5" />,
      collapsible: true,
      items: [
        { path: '/coveraty/orders', label: t('nav.storeOrders'), icon: <OrdersIcon className="w-5 h-5" />, roles: ['admin'] },
        { path: '/coveraty/designs', label: t('nav.designsManager'), icon: <LayoutIcon className="w-5 h-5" />, roles: ['admin'] },
        { path: '/coveraty/categories', label: t('nav.categories'), icon: <GridIcon className="w-5 h-5" />, roles: ['admin'] },
        { path: '/coveraty/attributes', label: t('nav.attributes'), icon: <LayersIcon className="w-5 h-5" />, roles: ['admin'] },
        { path: '/coveraty/shipping', label: t('nav.shipping'), icon: <TruckIcon className="w-5 h-5" />, roles: ['admin'] },
        { path: '/coveraty/customers', label: t('nav.storeCustomers'), icon: <ClientsIcon className="w-5 h-5" />, roles: ['admin'] },
      ]
    };

    const menu = [...baseItems];

    if (user.role === 'admin' || user.role === 'receptionist' || user.role === 'designer' || user.role === 'worker' || user.role === 'financial') {
      menu.push(salesSection);
    }

    if (user.role === 'admin' || user.role === 'worker') {
      menu.push(inventorySection);
    }

    if (user.role === 'admin' || user.role === 'financial' || user.role === 'receptionist') {
      menu.push(reportsSection);
    }

    if (user.role === 'admin') {
      menu.push(settingsSection);
      menu.push(coveratySection);
    }

    return menu;
  };

  const menuItems = getMenuItems();

  return (
    <>
      {/* Mobile backdrop overlay */}
      {isOpen && typeof window !== 'undefined' && window.innerWidth <= 768 && (
        <div
          className="sidebar-backdrop"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      <div
        className={`sidebar ${collapsed ? 'collapsed' : ''} ${isOpen ? 'open' : ''}`}
        style={isOpen && typeof window !== 'undefined' && window.innerWidth <= 768 ? { transform: 'translateX(0)', zIndex: 'calc(var(--z-overlay, 300) + 1)', display: 'flex' } : {}}
      >
        {/* Logo & Brand */}
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-brand">
            <div className="brand-logo">
              <Logo
                variant={collapsed ? 'icon' : 'full'}
                size="medium"
                alt={t('appName', language === 'ar' ? 'مجموعة سيندرولي' : 'Sendroli Group')}
              />
            </div>
            {!collapsed && (
              <div className="brand-text">
                <span className="brand-name">{t('appName')}</span>
                <span className="brand-tagline">{t('systemTitle')}</span>
              </div>
            )}
          </Link>
        </div>

        {/* User Info */}
        <div className="sidebar-user">
          <div className="user-avatar">
            {user.fullName?.charAt(0).toUpperCase() || 'U'}
          </div>
          {!collapsed && (
            <div className="user-info">
              <span className="user-name">{user.fullName}</span>
              <span className="user-role">{t(`role.${user.role}`, user.role)}</span>
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-nav">
          {menuItems.map((section, sectionIndex) => (
            <div key={sectionIndex} className="nav-section">
              {section.label && section.collapsible && !collapsed && (
                <div
                  className="section-header"
                  onClick={() => toggleSection(section.section)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSection(section.section);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={expandedSections[section.section]}
                >
                  <span className="section-label">{section.label}</span>
                  <span className={`section-arrow ${expandedSections[section.section] ? 'expanded' : ''}`}>
                    <ChevronDownIcon className="w-4 h-4 transition-transform duration-200" />
                  </span>
                </div>
              )}

              <ul
                className={`nav-items ${section.collapsible && !expandedSections[section.section] && !collapsed
                  ? 'collapsed'
                  : ''
                  }`}
              >
                {section.items
                  .filter((item) => item.roles.includes(user.role))
                  .map((item, itemIndex) => (
                    <li key={itemIndex} className="nav-item">
                      <Link
                        to={item.path}
                        className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                        title={collapsed ? item.label : ''}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                      >
                        {item.icon && (
                          <span className="nav-icon">
                            {item.icon}
                          </span>
                        )}
                        {!collapsed && <span className="nav-label">{item.label}</span>}
                        {isActive(item.path) && <span className="active-indicator" />}
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer with Language, Logout and Toggle */}
        <div className="sidebar-footer">
          <button
            type="button"
            className="lang-sidebar-btn flex items-center justify-center gap-2 p-2 rounded-lg border text-xs font-semibold transition"
            style={{
              borderColor: 'rgba(var(--theme-primary-rgb, 5, 150, 105), 0.35)',
              background: 'var(--surface, #fff)',
              color: 'var(--theme-primary)'
            }}
            onClick={toggleLanguage}
            title={language === 'ar' ? 'Switch to English' : 'التحويل للعربية'}
          >
            <GlobeIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} />
            {!collapsed && <span style={{ color: 'var(--theme-primary)', fontWeight: '700' }}>{language === 'ar' ? 'English' : 'العربية'}</span>}
          </button>
          <button className="logout-btn flex items-center justify-center gap-2" onClick={handleLogout} title={collapsed ? t('header.logout') : ''}>
            <LogoutIcon className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span>{t('header.logout')}</span>}
          </button>
          <button
            className="sidebar-toggle flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleCollapse();
            }}
            title={collapsed ? t('header.expandSidebar') : t('header.collapseSidebar')}
            aria-label={collapsed ? t('header.expandSidebar') : t('header.collapseSidebar')}
            type="button"
          >
            {collapsed ? (
              isRTL ? <ChevronLeftIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />
            ) : (
              isRTL ? <ChevronRightIcon className="w-4 h-4" /> : <ChevronLeftIcon className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
